
import { useEffect, useState } from 'react';

interface ContactProps {
  scrollY: number;
}

export default function Contact({ scrollY }: ContactProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('contact');
      if (element) {
        const rect = element.getBoundingClientRect();
        setIsVisible(rect.top < window.innerHeight * 0.7);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/contact-sacred-frequency', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus('Thank you for reaching out! We\'ll connect with you soon.');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('Something went wrong. Please try again.');
    }
    
    setIsSubmitting(false);
    setTimeout(() => setSubmitStatus(''), 5000);
  };

  const handleSubscribeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/newsletter-sacred-frequency', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email: subscribeEmail
        })
      });

      if (response.ok) {
        setSubmitStatus('Welcome to our sacred community! Check your email for a special gift.');
        setSubscribeEmail('');
      } else {
        setSubmitStatus('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('Something went wrong. Please try again.');
    }
    
    setIsSubmitting(false);
    setTimeout(() => setSubmitStatus(''), 5000);
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-amber-100 to-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-amber-800 to-red-700 bg-clip-text text-transparent">
            Connect with Sacred Frequency
          </h2>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed">
            Join our sacred community and stay connected with the latest rituals, wisdom, and offerings
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Newsletter Subscription */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="bg-white rounded-3xl p-8 shadow-2xl">
              <h3 className="text-3xl font-bold text-stone-800 mb-6">Join Our Sacred Circle</h3>
              <p className="text-stone-600 mb-8 leading-relaxed">
                Subscribe to receive sacred wisdom, ritual guides, and exclusive offers delivered to your inbox. 
                Plus, get a special welcome gift when you join our community.
              </p>
              
              <form onSubmit={handleSubscribeSubmit} data-readdy-form id="newsletter-sacred-frequency">
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    name="email"
                    value={subscribeEmail}
                    onChange={(e) => setSubscribeEmail(e.target.value)}
                    placeholder="Enter your sacred email"
                    required
                    className="flex-1 px-6 py-4 border border-stone-300 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-amber-600 to-red-600 text-white px-8 py-4 rounded-full font-semibold hover:from-amber-700 hover:to-red-700 transform hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? 'Joining...' : 'Join Circle'}
                  </button>
                </div>
              </form>

              <div className="mt-8 flex justify-center space-x-6">
                <a href="#" className="text-stone-400 hover:text-amber-600 transition-colors duration-300 cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-instagram-line text-2xl"></i>
                  </div>
                </a>
                <a href="#" className="text-stone-400 hover:text-amber-600 transition-colors duration-300 cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-facebook-line text-2xl"></i>
                  </div>
                </a>
                <a href="#" className="text-stone-400 hover:text-amber-600 transition-colors duration-300 cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-youtube-line text-2xl"></i>
                  </div>
                </a>
                <a href="#" className="text-stone-400 hover:text-amber-600 transition-colors duration-300 cursor-pointer">
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-pinterest-line text-2xl"></i>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="bg-white rounded-3xl p-8 shadow-2xl">
              <h3 className="text-3xl font-bold text-stone-800 mb-6">Send Us a Message</h3>
              <p className="text-stone-600 mb-8 leading-relaxed">
                Have questions about our products or need guidance on your wellness journey? 
                We're here to support you on your sacred path.
              </p>
              
              <form onSubmit={handleContactSubmit} data-readdy-form id="contact-sacred-frequency">
                <div className="space-y-6">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your Name"
                    required
                    className="w-full px-6 py-4 border border-stone-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="Your Email"
                    required
                    className="w-full px-6 py-4 border border-stone-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Your Message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-6 py-4 border border-stone-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none text-sm"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-amber-600 to-red-600 text-white py-4 rounded-2xl font-semibold hover:from-amber-700 hover:to-red-700 transform hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Sacred Message'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {submitStatus && (
          <div className="mt-8 text-center">
            <div className="inline-block bg-green-100 text-green-800 px-6 py-3 rounded-full font-medium">
              {submitStatus}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-20 pt-12 border-t border-stone-300">
          <div className="text-center">
            <div className="mb-6">
              <span className="text-3xl font-bold text-amber-700" style={{ fontFamily: '"Pacifico", serif' }}>
                Sacred Frequency
              </span>
            </div>
            <p className="text-stone-600 mb-6 max-w-2xl mx-auto leading-relaxed">
              Elevating consciousness through sacred rituals, divine herbs, and transformative wellness experiences. 
              Every product carries the frequency of love, intention, and healing.
            </p>
            <div className="flex flex-wrap justify-center gap-8 text-stone-500 text-sm">
              <span>© 2024 Sacred Frequency. All rights reserved.</span>
              <a href="#" className="hover:text-amber-600 transition-colors duration-300 cursor-pointer">Privacy Policy</a>
              <a href="#" className="hover:text-amber-600 transition-colors duration-300 cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?origin=logo" className="hover:text-amber-600 transition-colors duration-300 cursor-pointer">Website Builder</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
